import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage, ChatOpen, ChatReactionEmoji, Conversation, StickerId } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { api } from '../lib/api';
import type { Realtime } from './use-realtime';
import { notify } from '../lib/alerts';

// One open conversation: its messages, live updates, and the things you can do in it. The server's replies
// are the truth; a live event only adds or changes what the server already kept.
export function useConversation(
  target: ChatOpen | null,
  realtime: Realtime,
  options: { markRead?: boolean; myUserId?: string | null } = {},
) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [sending, setSending] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const targetKey = target ? JSON.stringify(target) : null;
  const markRead = options.markRead ?? true;
  const conversationRef = useRef<Conversation | null>(null);
  conversationRef.current = conversation;

  const load = useCallback(async () => {
    if (!target) {
      setConversation(null);
      setMessages([]);
      return;
    }
    setFailure(null);
    try {
      const reply = await api.chat.open(target);
      setConversation(reply.conversation);
      setMessages(reply.messages);
      setHasOlder(reply.messages.length >= 50);
      if (markRead && reply.conversation.kind === 'direct' && reply.messages.some((message) => !message.mine))
        void api.chat.read(reply.conversation.id).catch(() => {});
    } catch (error) {
      setFailure(describeError(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, markRead, realtime.generation]);
  useEffect(() => {
    void load();
  }, [load]);
  // live: subscribe while open; apply events for this conversation
  useEffect(() => {
    if (!conversation) return;
    const id = conversation.id;
    const unsubscribe = realtime.subscribe(id);
    const stop = realtime.listen((event) => {
      if (event.type === 'message' && event.message.conversationId === id) {
        // the live copy says "not mine" for everyone; my own (from this or another tab) is mine
        const incoming = {
          ...event.message,
          mine: !!options.myUserId && event.message.sender.userId === options.myUserId,
        };
        setMessages((current) =>
          current.some((message) => message.id === incoming.id) ? current : [...current, incoming],
        );
        if (markRead && conversation.kind === 'direct') void api.chat.read(id).catch(() => {});
      }
      if (event.type === 'edited' && event.message.conversationId === id)
        setMessages((current) =>
          current.map((message) =>
            message.id === event.message.id ? { ...event.message, mine: message.mine } : message,
          ),
        );
      if (event.type === 'deleted' && event.conversationId === id)
        setMessages((current) =>
          current.map((message) =>
            message.id === event.id ? { ...message, text: null, deletedAt: new Date().toISOString() } : message,
          ),
        );
      if (event.type === 'reacted' && event.conversationId === id)
        setMessages((current) =>
          current.map((message) => (message.id === event.id ? { ...message, reactions: event.reactions } : message)),
        );
      // the group changed (name, members): read it again
      if (event.type === 'group' && event.conversationId === id) void load();
      if (event.type === 'read' && event.conversationId === id)
        setMessages((current) =>
          current.map((message) =>
            message.mine && message.createdAt <= event.at ? { ...message, seen: true } : message,
          ),
        );
    });
    return () => {
      stop();
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, realtime]);

  const run = async <T>(work: () => Promise<T>) => {
    setSending(true);
    setFailure(null);
    try {
      return await work();
    } catch (error) {
      setFailure(describeError(error));
      return null;
    } finally {
      setSending(false);
    }
  };
  const send = async (text: string, replyTo: string | null, sticker?: StickerId) => {
    const current = conversationRef.current;
    if (!current) return false;
    const body = sticker ? { kind: 'sticker' as const, sticker, replyTo } : { kind: 'text' as const, text, replyTo };
    const reply = await run(() => api.chat.send(current.id, body, `chat-${crypto.randomUUID()}`));
    if (!reply) return false;
    // the live copy may have arrived first: the server's reply (mine, seen) replaces it
    setMessages((messages) =>
      messages.some((message) => message.id === reply.message.id)
        ? messages.map((message) => (message.id === reply.message.id ? reply.message : message))
        : [...messages, reply.message],
    );
    // answering a message request makes it an ordinary chat
    if (current.request) void load();
    return true;
  };
  const sendVoice = async (recording: Blob, type: string, ms: number) => {
    const current = conversationRef.current;
    if (!current) return false;
    const reply = await run(() => api.chat.voice(current.id, recording, type, ms, `voice-${crypto.randomUUID()}`));
    if (!reply) return false;
    setMessages((messages) =>
      messages.some((message) => message.id === reply.message.id)
        ? messages.map((message) => (message.id === reply.message.id ? reply.message : message))
        : [...messages, reply.message],
    );
    return true;
  };
  const allowVoice = async (allow: boolean) => {
    const current = conversationRef.current;
    if (!current) return;
    const reply = await run(() => api.chat.allowVoice(current.id, allow));
    if (reply) {
      setConversation(reply.conversation);
      notify(reply.message);
    }
  };
  const edit = async (messageId: string, text: string) => {
    const reply = await run(() => api.chat.edit(messageId, text));
    if (reply)
      setMessages((messages) => messages.map((message) => (message.id === messageId ? reply.message : message)));
    return Boolean(reply);
  };
  const remove = async (messageId: string) => {
    const reply = await run(() => api.chat.remove(messageId));
    if (reply)
      setMessages((messages) => messages.map((message) => (message.id === messageId ? reply.message : message)));
  };
  const react = async (messageId: string, emoji: ChatReactionEmoji | null) => {
    const reply = await run(() => api.chat.react(messageId, emoji));
    if (reply)
      setMessages((messages) => messages.map((message) => (message.id === messageId ? reply.message : message)));
  };
  const forward = async (messageId: string, conversationIds: string[]) => {
    const reply = await run(() => api.chat.forward(messageId, conversationIds, `fwd-${crypto.randomUUID()}`));
    if (reply) notify(reply.message);
    return Boolean(reply);
  };
  const report = async (messageId: string, reason: string) => {
    const reply = await run(() => api.chat.report(messageId, reason));
    if (reply) notify(reply.message);
  };
  const clear = async (hide: boolean) => {
    const current = conversationRef.current;
    if (!current) return;
    const ok = await run(() => api.chat.clear(current.id, hide));
    if (ok) setMessages([]);
    return Boolean(ok);
  };
  const loadOlder = async () => {
    const current = conversationRef.current;
    const first = messages[0];
    if (!current || !first) return;
    const reply = await run(() => api.chat.older(current.id, first.createdAt));
    if (reply) {
      setMessages((messages) => [
        ...reply.messages.filter((older) => !messages.some((message) => message.id === older.id)),
        ...messages,
      ]);
      setHasOlder(reply.messages.length >= 50);
    }
  };
  // a money card sent or changed from here: shown at once (the live copy may come a moment later)
  const put = (message: ChatMessage) =>
    setMessages((messages) =>
      messages.some((item) => item.id === message.id)
        ? messages.map((item) => (item.id === message.id ? message : item))
        : [...messages, message],
    );
  return {
    conversation,
    messages,
    put,
    failure,
    sending,
    hasOlder,
    send,
    sendVoice,
    allowVoice,
    edit,
    remove,
    react,
    forward,
    report,
    clear,
    loadOlder,
    reload: load,
  };
}
